/**
 * A round action button with a picture on it — the pet panel's care row, and a few one-offs.
 *
 * **`art` is the picture; `emoji` is the fallback and the text form.** The care actions are
 * painted now (`CARE_ACTION_ART`), but the glyph stays on the props because a handful of call
 * sites have no painted asset and because the emoji is still what a tooltip, a log line and a
 * Telegram message print. Pass both; the button prefers the art.
 */
import { Button } from "@radix-ui/themes";
import { ReactNode } from "react";
import { GameIcon } from "./GameIcon";

interface EmojiActionButtonProps {
  emoji: string;
  /** A `src` from `shared/domain/uiIconArt.ts`. Drawn instead of `emoji` when present. */
  art?: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  variant?: 'default' | 'primary' | 'accent' | 'danger' | 'evolve';
  size?: 'small' | 'medium' | 'large';
  children?: ReactNode;
}

export function EmojiActionButton({
  emoji,
  art,
  onClick,
  disabled = false,
  title,
  variant = 'default',
  size = 'medium',
  children
}: EmojiActionButtonProps) {
  const sizeClass = size === 'small' ? 'emoji-action-btn--small'
    : size === 'large' ? 'emoji-action-btn--large'
    : '';
  const variantClass = variant !== 'default' ? `emoji-action-btn--${variant}` : '';

  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`emoji-action-btn ${sizeClass} ${variantClass}`.trim()}
    >
      <span className="emoji-action-btn__emoji">
        {art ? <GameIcon src={art} fallback={emoji} size="action" /> : emoji}
      </span>
      {children && <span className="emoji-action-btn__label">{children}</span>}
    </Button>
  );
}
